import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import _ from 'lodash';
import ZoomContext from 'zoom/context/zoom-context';
import ZoomMediaContext from 'zoom/context/media-context';
import { useNavigate } from 'react-router-dom';
import { isShallowEqual } from 'utils/util';
import { useAppDispatch } from 'redux/hooks/reduxHooks';
import {
  publishingBaseURL,
  mediaServerPublishingAuthorizationQuery,
} from 'shared/constants/stream-constants';
import { useEndDebateMutation, useGetCurrentDebateQuery } from 'redux/api/debate.api';
import { useLoading } from 'shared/hooks/useLoading';
import { EDebateStatuses } from 'shared/types/debates.interface';
import { useSizeCallback } from 'zoom/hooks/useSizeCallback';
import { Box } from '@mui/material';
import { PageContainer } from 'pages/PageContainer';
import { FullCardDescription } from 'pages/Main/FullCard/FullCardDescription/FullCardDescription';
import { DEBATE_ENDING_OR_LEAVING } from 'shared/constants/alert-constants';
import { urls } from 'shared/constants/routing.constants';
import { LOCAL_STORAGE } from 'shared/constants/local-storage-constants';
import DebateSkeleton from 'components/Skeletons/DebateSkeleton/DebateSkeleton';
import { useRemainingTime } from 'shared/hooks/useRemainingTime';
import { EPageTypes } from 'shared/types/common.interface';
import { handleSnackbarNotify } from 'utils/handleSnackbarNotify';
import { useAuth } from 'shared/guards/useAuth';
import { useNotFoundRedirect } from 'shared/guards/useNotFoundRedirect';
import { useRestartDebate } from 'shared/guards/useRestartDebate';
import { LiveStreamStatus } from '@zoom/videosdk';
import { useCanvasDimension } from './hooks/useCanvasDimension';
import { useGalleryLayout } from './hooks/useGalleryLayout';
import { usePagination } from './hooks/usePagination';
import { useActiveVideo } from './hooks/useAvtiveVideo';
import { useShare } from './hooks/useShare';
import { useLocalVolume } from './hooks/useLocalVolume';
import { useAdvancedFeatureSwitch } from './hooks/useAdvancedFeatureSwith';
import { useCameraControl } from './hooks/useCameraControl';
import { useNetworkQuality } from './hooks/useNetworkQuality';
import VideoFooter, { VideoFooterProps } from './Video-footer/Video-footer';
import ZoomAvatar from './ZoomAvatar/ZoomAvatar';

interface VideoContainerProps {
  footerProps: VideoFooterProps;
}

const VideoContainer = (props: VideoContainerProps) => {
  useAuth();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [endDebate, { isLoading }] = useEndDebateMutation();
  useLoading(isLoading);

  const { footerProps } = props;

  // getting the debateId from localStorage
  const debateId = localStorage.getItem(LOCAL_STORAGE.currentDebateId);

  const { data: debate = null, error: debateError } = useGetCurrentDebateQuery(debateId, {
    pollingInterval: 1000,
    refetchOnMountOrArgChange: true,
    skip: !debateId,
  });
  useNotFoundRedirect(debateError, !debateId);
  useRestartDebate();
  useRemainingTime();

  // getting zoom client here
  const zmClient = useContext(ZoomContext);

  const hostData = zmClient.getSessionHost();
  // updating information about session state and host state
  const { sessionId } = zmClient.getSessionInfo();

  const isHost = zmClient?.isHost();

  // getting status of various media related content, and the stream itself
  const {
    mediaStream,
    video: { decode: isVideoDecodeReady },
  } = useContext(ZoomMediaContext);

  // constructing refs for video
  const videoRef = useRef<(HTMLCanvasElement & HTMLVideoElement) | null>(null);

  // constructing refs for sharing functionality
  const shareRef = useRef<HTMLCanvasElement | null>(null);
  const selfShareRef = useRef<(HTMLCanvasElement & HTMLVideoElement) | null>(null);
  const shareContainerRef = useRef<HTMLDivElement | null>(null);

  // setting initial container size of videocontainer
  const [containerDimension, setContainerDimension] = useState({
    width: 0,
    height: 0,
  });

  // setting sharing container size size
  const [shareViewDimension, setShareViewDimension] = useState({
    width: 0,
    height: 0,
  });

  // This gets the dimension of canvas, and monitors its size constantly, contains 2D rectangle
  const canvasDimension = useCanvasDimension(mediaStream, videoRef);

  // thsi is a hook controlling the active video, and contains current video number
  const activeVideo = useActiveVideo(zmClient);

  // this is a hook handling page sizes mostly, also manages current page
  // page is current page, page size is amount of users possible on page,
  // totalPage is amount of pages needed for users, totalSize is amount of current
  // users
  const { page, pageSize, totalPage, totalSize } = usePagination(zmClient);

  const { participantsLayout } = useGalleryLayout(
    zmClient,
    mediaStream,
    isVideoDecodeReady,
    videoRef,
    canvasDimension,
    {
      page,
      pageSize,
      totalPage,
      totalSize,
    },
  );
  const { isRecieveSharing, isStartedShare, sharedContentDimension } = useShare(
    zmClient,
    mediaStream,
    shareRef,
  );

  const { userVolumeList, setLocalVolume } = useLocalVolume();
  const { isControllingFarEnd, giveUpControl } = useCameraControl(zmClient, mediaStream);

  const { advancedSwitch, toggleAdjustVolume } = useAdvancedFeatureSwitch(zmClient, mediaStream);
  const networkQuality = useNetworkQuality(zmClient);

  const isSharing = isRecieveSharing || isStartedShare;

  const leaveDebate = useCallback(
    async (message: string, pageToNavigate?: string) => {
      if (!debate) return;
      // if user is host - end debate (arg = true), if he isn't - leave debate (arg = undefind)
      await zmClient.leave(zmClient.isHost() || undefined);
      if (footerProps.setLogged) footerProps.setLogged(false);

      if (debate.status === EDebateStatuses.InProgress) {
        endDebate({
          id: debate.id,
          message,
          status: debate.status,
        });
      } else {
        handleSnackbarNotify(dispatch, message, 'info');
        localStorage.removeItem(LOCAL_STORAGE.currentDebateId);
      }

      navigate(pageToNavigate || `${urls.commonPages.debateCard}/${debate.id}`);
    },
    [debate, zmClient, footerProps, navigate, endDebate, dispatch],
  );

  useEffect(() => {
    if (isSharing && shareContainerRef.current) {
      const { width, height } = sharedContentDimension;
      const { width: containerWidth, height: containerHeight } = containerDimension;
      const ratio = Math.min(containerWidth / width, containerHeight / height, 1);
      setShareViewDimension({
        width: Math.floor(width * ratio),
        height: Math.floor(height * ratio),
      });
    }
  }, [isSharing, sharedContentDimension, containerDimension]);

  const onShareContainerResize = useCallback(({ width, height }) => {
    _.throttle(() => {
      setContainerDimension({ width, height });
    }, 50)();
  }, []);
  useSizeCallback(shareContainerRef.current, onShareContainerResize);
  useEffect(() => {
    if (!isShallowEqual(shareViewDimension, sharedContentDimension)) {
      mediaStream?.updateSharingCanvasDimension(
        shareViewDimension.width,
        shareViewDimension.height,
      );
    }
  }, [mediaStream, sharedContentDimension, shareViewDimension]);

  const onAdvancedFeatureToggle = useCallback(
    (toggleUserId: number, key: string) => {
      if (key === 'volume') {
        toggleAdjustVolume(toggleUserId);
      } else if (key === 'farend') {
        if (isControllingFarEnd) {
          giveUpControl();
        } else {
          mediaStream?.requestFarEndCameraControl(toggleUserId);
        }
        // toggleFarEndCameraControl(userId);
      }
    },
    [toggleAdjustVolume, giveUpControl, mediaStream, isControllingFarEnd],
  );

  /**
   * This react effect function is checking if the current user is host, session id is retrieved and  host data
   * is available.
   * If all this happened, it gets a livestream client, and starts a stream according to the current sessionID
   * @param sessionId, @param isHost and @param hostData are bind here through a context
   * @returns a function cleaner, which stops the stream when effect is unmounted, or nothing,
   *  if the stream wasn't started
   */
  const publishStreamIfHost = useCallback(() => {
    // TODO: revise the checkups, which are ugly, but this works only with all these three
    // I would like to add a checking if the stream is already published, we'll see
    if (sessionId) {
      if (isHost) {
        // getting the client
        const livestreamClient = zmClient.getLiveStreamClient();

        const livestreamStatus = livestreamClient.getLiveStreamStatus();
        if (livestreamStatus === LiveStreamStatus.Ended) {
          // TODO: THIS SHOULD BE REMOVED TOO
          // forming the url
          const playbackURL = new URL(
            `${window.location.origin}${urls.commonPages.stream}/${debateId}`,
          );
          // strating the stream mostly
          livestreamClient.startLiveStream(
            publishingBaseURL,
            `${debateId}${mediaServerPublishingAuthorizationQuery}`,
            playbackURL.toString(),
          );

          return () => {};
        }
      }
    }

    return null;
  }, [debateId, sessionId, zmClient, isHost]);

  // publishing the stream as an effect, dependent on all conditions,
  // see the function @{publishStreamIuserIdfHost} description
  useEffect(
    () => publishStreamIfHost(),
    [debateId, hostData, isHost, publishStreamIfHost, sessionId, zmClient],
  );

  useEffect(() => {
    if (!debate) return;

    switch (debate?.status) {
      case EDebateStatuses.Finished:
        leaveDebate(DEBATE_ENDING_OR_LEAVING.finished);
        break;
      case EDebateStatuses.Left:
        leaveDebate(DEBATE_ENDING_OR_LEAVING.yourOpponentLeft);
        break;
      case EDebateStatuses.Failed:
        leaveDebate(DEBATE_ENDING_OR_LEAVING.failed);
        break;
      default:
        break;
    }
  }, [debate, leaveDebate]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      return true;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [debateId]);

  if (isLoading || !debate || !mediaStream || !zmClient?.getSessionInfo()?.isInMeeting) {
    return (
      <PageContainer>
        <DebateSkeleton type={EPageTypes.debate} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box>
        <Box
          width="100%"
          position="relative"
          sx={{
            height: { md: '30vw', xs: '106vw' },
          }}
        >
          {participantsLayout.map(({ layout }, index) => (
            <Box // grey background for each video cell area
              key={index}
              sx={{
                position: 'absolute',
                left: `${layout.x}px`,
                bottom: `${layout.y}px`,
                width: `${layout.width}px`,
                height: `${layout.height}px`,
                backgroundColor: 'primary.main',
                borderRadius: '8px',
                zIndex: 1,
              }}
            />
          ))}
          <canvas
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 2,
            }}
            id="video-canvas"
            ref={videoRef}
          />
          <Box
            component="ul"
            sx={{
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              padding: 0,
              margin: 0,
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 3,
            }}
          >
            {participantsLayout.map(({ user, layout }) => {
              const { width, height, x, y } = layout;
              return (
                <ZoomAvatar
                  participant={user}
                  debate={debate}
                  key={user.userId}
                  isActive={activeVideo === user.userId}
                  volume={userVolumeList.find((u) => u.userId === user.userId)?.volume}
                  setLocalVolume={setLocalVolume}
                  advancedFeature={advancedSwitch[`${user.userId}`]}
                  onAdvancedFeatureToggle={onAdvancedFeatureToggle}
                  isUserCameraControlled={isControllingFarEnd}
                  networkQuality={networkQuality[`${user.userId}`]}
                  style={{
                    width: `${width}px`,
                    height: `${height}px`,
                    bottom: `${y}px`,
                    left: `${x}px`,
                  }}
                />
              );
            })}
          </Box>
        </Box>
        <FullCardDescription
          challengeData={debate.challenge}
          debateData={debate}
          mt={{ lg: '119px', md: '64px', xs: '42px' }}
        >
          <VideoFooter
            sharing
            shareRef={selfShareRef}
            setLogged={footerProps.setLogged}
            isHost={isHost}
            leaveDebate={leaveDebate}
            captureWidth={participantsLayout[0]?.layout.width}
            captureHeight={participantsLayout[0]?.layout.height}
          />
        </FullCardDescription>
      </Box>
    </PageContainer>
  );
};

export default VideoContainer;
